import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import PricingModalButton from '../../organization/PricingModalButton';
import SettingCard from '../../settings/SettingCard';
import getFeature from '../../utils/features';
import nonEmpty from '../../utils/nonEmpty';
import * as styles from './PublicContentSettings.module.scss';

const OPTIONS = {
  ALLOW_ALL: 'Allow all iframe hosts',
  ALLOW_SPECIFIC: 'Allow specific iframe hosts',
  DISABLED: 'Disable unintegrated iframe hosts',
} as const;

const SETTING_FRAGMENT = gql(`
  fragment AllowedIframeHostsSettings_settings on OrganizationSettings {
    allowedEmbedHosts
  }
`);

const UPDATE_ALLOWED_IFRAME_HOSTS_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateAllowedIframeHostsSettings($allowedEmbedHosts: [String!]!) {
    organization: updateOrganization(settings: { allowedEmbedHosts: $allowedEmbedHosts }) {
      id
      settings {
        ...AllowedIframeHostsSettings_settings
      }
    }
  }
`);

type Values = {
  allowed: keyof typeof OPTIONS;
  hosts: string;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const parseHosts = (hostsString: string | null): string[] =>
  hostsString
    ?.split(',')
    .map((host) => {
      try {
        const trimmed = host.trim();
        return new URL(
          /^https?:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`,
        ).host;
      } catch (e) {
        return '';
      }
    })
    .filter(Boolean) ?? [];

const joinHosts = (hosts: Array<string | null | undefined> | null): string =>
  hosts?.filter(nonEmpty).join(', ') ?? '';

interface AllowedIframeHostsSettingsProps {
  settings: DocumentType<typeof SETTING_FRAGMENT>;
}

const AllowedIframeHostsSettings = ({
  settings,
}: AllowedIframeHostsSettingsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateAllowedIframeHostsSettings] = useMutation(
    UPDATE_ALLOWED_IFRAME_HOSTS_SETTINGS_MUTATION,
  );

  const handleSubmit = async (values: Values) => {
    let allowedEmbedHosts: string[] | null = null;

    if (values.allowed === 'ALLOW_ALL') {
      allowedEmbedHosts = ['*'];
    } else if (values.allowed === 'DISABLED') {
      allowedEmbedHosts = [];
    } else {
      allowedEmbedHosts = parseHosts(values.hosts);
    }

    await updateAllowedIframeHostsSettings({
      variables: { allowedEmbedHosts },
    });
    window.__slab.PUBLIC_ORG.settings.allowedEmbedHosts = allowedEmbedHosts;
    setIsEditing(false);
  };

  const isEditorIFrameEnabled = getFeature('editor_iframe');

  const initialValues: Values = useMemo<Values>(() => {
    const { allowedEmbedHosts } = settings;
    if (
      !allowedEmbedHosts ||
      allowedEmbedHosts.length === 0 ||
      !isEditorIFrameEnabled
    ) {
      return { allowed: 'DISABLED', hosts: '' };
    }
    if (allowedEmbedHosts.length === 1 && allowedEmbedHosts[0] === '*') {
      return { allowed: 'ALLOW_ALL', hosts: '' };
    }
    return { allowed: 'ALLOW_SPECIFIC', hosts: joinHosts(allowedEmbedHosts) };
  }, [settings, isEditorIFrameEnabled]);

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Allowed iframe hosts"
          description={
            <Form<Values>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange
              validate={(values) =>
                values.allowed === 'ALLOW_SPECIFIC' &&
                parseHosts(values.hosts).length === 0
                  ? { hosts: 'Please include at least one host' }
                  : {}
              }
            >
              {({ values }) => (
                <>
                  <div className={styles.formFields}>
                    <Field
                      label={OPTIONS.ALLOW_ALL}
                      name="allowed"
                      type="radio"
                      value="ALLOW_ALL"
                    />
                    <Field
                      label={OPTIONS.ALLOW_SPECIFIC}
                      name="allowed"
                      type="radio"
                      value="ALLOW_SPECIFIC"
                    />
                    {values.allowed === 'ALLOW_SPECIFIC' && (
                      <Field
                        required
                        type="text"
                        name="hosts"
                        placeholder="Enter hosts separated by commas (e.g. share.getcloudapp.com, loom.com)"
                      />
                    )}
                    <Field
                      label={OPTIONS.DISABLED}
                      name="allowed"
                      type="radio"
                      value="DISABLED"
                    />
                  </div>
                  <div className={styles.formFooter}>
                    <Submit variant="primary">Save</Submit>
                  </div>
                </>
              )}
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            isEditorIFrameEnabled ? (
              <Button onClick={() => setIsEditing(true)} variant="secondary">
                Edit
              </Button>
            ) : (
              <PricingModalButton feature="customEmbedHosts">
                Upgrade
              </PricingModalButton>
            )
          }
          title="Allowed iframe hosts"
          description={OPTIONS[initialValues.allowed]}
        />
      )}
    </>
  );
};

export default AllowedIframeHostsSettings;
